import Link from "next/link"

interface ErrorStateProps {
  /** Displayed numeral, e.g. "404". Rendered in Playfair. */
  code: string
  /** Small uppercase line above the title. */
  eyebrow: string
  title: string
  /** Emphasised fragment of the title, rendered in gold. */
  titleAccent?: string
  description: string
  /** Secondary destinations shown under the actions. */
  links?: Array<{ href: string; label: string }>
}

/**
 * Branded full-screen error state used by the public 404 and route error
 * boundaries. Follows the `.home` design language documented in DESIGN.md —
 * navy surface, gold hairline, Playfair headings — rather than inventing
 * one-off hex values.
 */
export function ErrorState({
  code,
  eyebrow,
  title,
  titleAccent,
  description,
  links = [],
}: ErrorStateProps) {
  return (
    <section className="home-navy relative flex min-h-screen items-center justify-center overflow-hidden px-6 py-32">
      {/* Ambient gold glow, matching the events hero. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-auto -right-[15%] top-[10%] h-[30rem] w-[30rem] rounded-full bg-[radial-gradient(circle,color-mix(in_oklch,var(--home-gold)_16%,transparent)_0%,transparent_68%)]"
      />

      <div className="home-wrap relative z-10 flex flex-col items-center text-center">
        <span className="home-rule" aria-hidden />

        <p className="mt-8 text-xs uppercase tracking-[0.28em] text-[var(--home-gold)]">{eyebrow}</p>

        <p
          aria-hidden
          className="mt-6 font-heading text-[clamp(4.5rem,16vw,11rem)] font-medium leading-[0.85] tracking-[-0.04em] text-[color-mix(in_oklch,var(--home-cream)_22%,transparent)]"
        >
          {code}
        </p>

        <h1 className="mt-8 max-w-3xl font-heading text-[clamp(2rem,5vw,3.4rem)] font-medium leading-[1.05] tracking-[-0.03em] text-[var(--home-cream)]">
          {title} {titleAccent && <span className="text-[var(--home-gold)]">{titleAccent}</span>}
        </h1>

        <p className="home-muted mt-6 max-w-xl text-base leading-relaxed">{description}</p>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
          <Link href="/" className="home-btn home-btn-gold">
            Back to Home
          </Link>
          <Link href="/contact" className="home-btn home-btn-ghost">
            Contact Us
          </Link>
        </div>

        {links.length > 0 && (
          <nav aria-label="Suggested pages" className="mt-16 border-t border-[color-mix(in_oklch,var(--home-gold)_22%,transparent)] pt-8">
            <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
              {links.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} className="home-link text-sm text-[var(--home-cream)]">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </section>
  )
}